<template>
  <v-text-field
    ref="numberInputRef"
    v-bind="$attrs"
    :value="formattedValue"
    :hide-details="hideDetails"
    :rules="processedRules"
    @input="inputHandler"
    @blur="handleBlur"
  />
</template>

<script lang="ts">
import mixins from 'vue-typed-mixins';
import { PropType } from 'vue';

import { Nullable } from '@/utils/types';
import { formatNumber, formatRawNumber } from '@/utils/filters';
import { numberFormatter, NumberInputEnum } from '@/bundles/Common/types/numberInput';
import { NumberInputMixin } from '@/bundles/Common/mixins/NumberInputMixin';
import { CursorManagementMixin } from '@/bundles/Common/mixins/CursorManagementMixin';
import { isValuePresent } from '@/bundles/App/helpers/isValuePresent';

export default mixins(NumberInputMixin, CursorManagementMixin).extend({
  name: 'BaseNumberInput',

  props: {
    formatType: {
      type: String as PropType<NumberInputEnum>,
      default: NumberInputEnum.number_cut_zero
    },
    rules: {
      type: Array as PropType<Function[]>,
      default: () => []
    },
    isRawFormat: Boolean,
    decimals: {
      type: Number,
      default: 2
    },
  },

  data: (): {
    formattedValue: string,
    lastResult: Nullable<number>
  } => ({
    formattedValue: '',
    lastResult: null
  }),

  computed: {
    processedRules (): Function[] {
      return this.rules.map(rule => () => {
        const rawValue = isValuePresent(this.lastResult) ? this.lastResult : this.value;
        return rule(rawValue);
      });
    }
  },

  watch: {
    value: {
      handler (newValue: Nullable<string | number>) {
        const isClearingValue = !isValuePresent(newValue) && !isValuePresent(this.lastResult);

        if (!isClearingValue && this.lastResult === newValue) {
          return;
        }

        this.formatValue();
      },
      immediate: true
    },
    decimals () {
      this.formatValue();
    }
  },

  methods: {
    handleBlur () {
      if (!isValuePresent(this.lastResult)) return;

      if (this.formatType === NumberInputEnum.number_cut_zero) {
        this.formattedValue = this.isRawFormat
          ? this.lastResult!.toString()
          : numberFormatter[this.formatType](this.lastResult); // cut trailing zeros
        this.setModelValueOnBlur();
        this.updateInputProgrammatically(this.formattedValue);
        return;
      }

      const decimalTypes = [NumberInputEnum.decimal, NumberInputEnum.currency, NumberInputEnum.percent];
      if (decimalTypes.includes(this.formatType)) { // show decimals (default 2)
        const [intPart, decPart] = this.lastResult!.toFixed(this.decimals).split('.');

        // add % sign for percent type
        const appendChar = this.formatType === NumberInputEnum.percent ? '%' : '';

        this.formattedValue = this.isRawFormat
          ? `${intPart}.${decPart}${appendChar}`
          : `${formatNumber(intPart)}.${decPart}${appendChar}`;
        this.setModelValueOnBlur();
        this.updateInputProgrammatically(this.formattedValue);
      }
    },
    setModelValueOnBlur () {
      const inputValue = String(this.formattedValue).replace(/[^\d.]/g, '');
      this.$emit('input', formatRawNumber(inputValue));
    },
    inputHandler (value: Nullable<string>) {
      if (!isValuePresent(value)) {
        this.formattedValue = '';
        this.$emit('input', null);
        this.lastResult = null;
        this.updateInputProgrammatically(this.formattedValue);
        return;
      }

      this.saveCursorPosition();
      const stringValue = String(value).replace(/[^\d.]/g, ''); // remove all not number characters

      const hasMultipleDecimals = (stringValue.match(/\./g) || []).length > 1;
      if (hasMultipleDecimals) { // ignore input if multiple decimals
        this.updateInputProgrammatically(this.formattedValue);
        this.$nextTick(() => {
          this.restoreCursorPosition(this.inputValueAtCursor, this.formattedValue);
        });
        return;
      }

      // check if user just typed a decimal point or has partial decimal
      const endsWithDecimal = stringValue.endsWith('.');
      const rawValue = formatRawNumber(stringValue);

      this.$emit('input', rawValue);
      this.lastResult = rawValue;

      // if typing decimal point, preserve it temporarily
      if (endsWithDecimal && this.formatType !== NumberInputEnum.integer) {
        // Format with commas but preserve the trailing decimal point
        let formatted = this.formatNumberPreservingDecimals(stringValue);
        if (this.formatType === NumberInputEnum.percent) {
          formatted += '%';
        }
        this.formattedValue = formatted;
        this.updateInputProgrammatically(this.formattedValue);
        this.$nextTick(() => {
          this.restoreCursorPosition(this.inputValueAtCursor, this.formattedValue);
        });
      } else {
        // format and restore cursor - preserve trailing zeros during typing
        this.formatValueWithCursorPreservingDecimals(stringValue);
      }
    },
    formatValueWithCursorPreservingDecimals (stringValue: string) {
      if (!stringValue || stringValue === '') {
        this.formattedValue = '';
        this.updateInputProgrammatically(this.formattedValue);
        return;
      }

      if (this.isRawFormat) {
        const rawNumber = formatRawNumber(stringValue);
        this.formattedValue = rawNumber!.toString();
      } else {
        // preserve trailing zeros during typing for these formats
        const decimalTypes = [NumberInputEnum.number_cut_zero, NumberInputEnum.decimal, NumberInputEnum.currency, NumberInputEnum.percent];
        if (decimalTypes.includes(this.formatType)) {
          const formatted = this.formatNumberPreservingDecimals(stringValue);
          const appendChar = this.formatType === NumberInputEnum.percent ? '%' : '';
          this.formattedValue = `${formatted}${appendChar}`;
        } else { // for integer use standard formatting
          const rawNumber = formatRawNumber(stringValue);
          this.formattedValue = numberFormatter[this.formatType](rawNumber)!.toString();
        }
      }

      this.updateInputProgrammatically(this.formattedValue);
      this.$nextTick(() => { // restore cursor position using the OLD formatted value
        this.restoreCursorPosition(this.inputValueAtCursor, this.formattedValue);
      });
    },
    formatValue () {
      // on blur, use lastResult if no immediate value provided
      // lastResult is the most recent value we emitted during typing
      const valueToFormat = isValuePresent(this.lastResult) ? this.lastResult : this.value;

      if (!isValuePresent(valueToFormat)) {
        this.formattedValue = '';
        this.updateInputProgrammatically(this.formattedValue);
        return;
      }

      // use standard formatting - this applies the proper format on blur
      if (this.isRawFormat) {
        this.formattedValue = formatRawNumber(valueToFormat)!.toString();
      } else {
        // Handle custom decimals for decimal/currency/percent types
        const decimalTypes = [NumberInputEnum.decimal, NumberInputEnum.currency, NumberInputEnum.percent];
        if (decimalTypes.includes(this.formatType)) {
          const [intPart, decPart] = Number(valueToFormat).toFixed(this.decimals).split('.');
          const appendChar = this.formatType === NumberInputEnum.percent ? '%' : '';
          this.formattedValue = `${formatNumber(intPart)}.${decPart}${appendChar}`;
        } else {
          this.formattedValue = numberFormatter[this.formatType](valueToFormat);
        }
      }
      this.updateInputProgrammatically(this.formattedValue);
    }
  }
});
</script>
